import { useCallback, useContext } from 'react';

import { LocalizationContext } from '@core/context';
import { useProjectStore, useSuperHubStore } from '@core/store';

interface Args {
  /**
   * The hash to append to the end of the URL. This is useful for linking to
   * hash-router based panels like settings and versions. This can be prefixed
   * with "#" or not, and the hook will ensure that it is built correctly.
   *
   * @example `versions` or `#versions`
   */
  hash?: string;
  /**
   * Whether to prepend the base URL to the route. This is useful for hard
   * browser redirects that happen outside of the context of react-router, to
   * ensure we don't lose segments of the react-router base URL like the child
   * subdomains and language.
   */
  includeBaseUrl: boolean;
  /**
   * The segments of the pathname to build. These can be prefixed or suffixed with
   * "/" freely or not, and the hook will ensure that they are joined cleanly
   * with only a single "/" between them.
   *
   * @example
   * `['v1.0', 'docs/', '/getting-started']`
   */
  pathnameSegments: (string | false | undefined)[];
}

/**
 * This hook returns a function that builds a route based on the provided
 * arguments. It handles optionally prepending the base URL, safely joining the
 * provided pathname segments with a single "/", and optionally appending a hash
 * to the end of the URL.
 *
 * @example
 * const buildRoute = useBuildRoute();
 *
 * // returns "/subdomain/lang-es/v1.0/docs/getting-started#versions"
 * const routeWithBaseUrl = buildRoute({
 *   includeBaseUrl: true,
 *   pathnameSegments: ['/v1.0', '/docs/', '/getting-started'],
 *   hash: 'versions'
 * });
 *
 * // returns "/v1.0/docs/getting-started#versions"
 * const routeWithoutBaseUrl = buildRoute({
 *   includeBaseUrl: false,
 *   pathnameSegments: ['v1.0/', 'docs/', '/getting-started'],
 *   hash: 'versions'
 * });
 */
export default function useBuildRoute() {
  const isEnterpriseChild = useSuperHubStore(s => s.enterprise?.isChild);
  const subdomain = useProjectStore(s => s.data.subdomain);
  const language = useContext(LocalizationContext);

  return useCallback(
    ({ includeBaseUrl, pathnameSegments, hash }: Args) => {
      const subdomainSegment = isEnterpriseChild && subdomain;
      const languageSegment = language.length && language !== 'en' && `lang-${language}`;

      // only include the base URL segments if the consumer has requested it
      const baseUrlSegments = includeBaseUrl ? [subdomainSegment, languageSegment] : [];
      // filter out any falsey segments
      const stringSegments = [...baseUrlSegments, ...pathnameSegments].filter(Boolean) as string[];
      // remove leading and trailing slashes from each segment and join them
      // with a single slash
      const joinedSegments = stringSegments.map(segment => segment.replace(/^\//, '').replace(/\/$/, '')).join('/');
      // remove any leading slash from the hash
      const hashSegment = hash ? `#${hash.replace(/^#/, '')}` : '';

      return `/${joinedSegments}${hashSegment}`;
    },
    [isEnterpriseChild, language, subdomain],
  );
}
